import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Switch } from "@volca/ui/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { type APPROVAL_GATES } from "./approval-types";
import { describeApprovalProvenance } from "./utils";

interface ApprovalToggleProps {
  gate: (typeof APPROVAL_GATES)[number];
  subject: Pick<IPayrollSheetApprovalAccessKey, "name" | "approvals">;
  approvalAuthorized: boolean;
  isPending: boolean;
  isBusy: boolean;
  onChange?: (enabled: boolean) => void;
}

/**
 * One gate for one approval unit.
 *
 * Turning it ON submits immediately; turning it OFF routes through a
 * confirmation that can carry a reason, since withdrawing a sign-off invalidates
 * work other people may already have acted on. Left disabled rather than hidden
 * for a reader without approval access: they still need to see where the period
 * stands, they just can't move it.
 */
export const ApprovalToggle = ({
  gate,
  subject,
  approvalAuthorized,
  isPending,
  isBusy,
  onChange,
}: ApprovalToggleProps) => {
  const { label } = gate;
  const approval = subject.approvals[gate.type];
  const isApproved = approval.approved;
  const canChange = approvalAuthorized && Boolean(onChange);
  // The matrix has no room for a provenance line, so the hint carries it —
  // hovering a mark says who settled it without opening anything.
  const provenance =
    !isApproved && (approval.total_count ?? 0) > 1
      ? `${approval.approved_count ?? 0} of ${approval.total_count} sheets approved`
      : describeApprovalProvenance(approval);
  const action = canChange
    ? isApproved
      ? "Switch off to take it back."
      : "Switch on to approve."
    : "You can see these sheets, but you can't approve them.";
  const hint = [
    `${label}: ${isApproved ? "approved" : "not approved yet"}`,
    provenance,
    action,
  ]
    .filter(Boolean)
    .join(". ")
    .replace(/\.\./g, ".");

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Stack
            direction="row"
            align="center"
            justify="end"
            gap={2}
            className="w-14 shrink-0"
          />
        }
      >
        {isPending && <Spinner aria-hidden size={14} className="text-brand" />}
        <Switch
          aria-label={`${label} for ${subject.name}`}
          checked={isApproved}
          disabled={!canChange || isBusy}
          onCheckedChange={(enabled) => onChange?.(enabled)}
          className={cn({ "cursor-default": !canChange })}
        />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  );
};
